import { Checkbox, Form, Spin, Typography } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import type { ModelSocialIntegration } from '@app/api/SocialIntegrations';
import { ATC_BEELINE, ATC_MANGO, ERROR_REQUIRED_FIELD } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';
import {
  useCheckMangoTokenAndListAbonetsQuery,
  useLazyGetAtcBeelineAbonentsQuery,
} from '@app/services/rtkQuery/SocialIntegrations/api';
import type { NSocialIntegrations } from '@app/services/rtkQuery/SocialIntegrations/types';

import { BeelineAbonentsSelector } from '@pages/Integrations/IntegrationsList/components/BeelineAbonentsSelector';

import type { CheckboxProps } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import type { FormInstance } from 'antd/lib';

const { Text } = Typography;

type AtcPhoneNumbersBlockProps = {
  activeBranchId: number | null;
  setSelectedPhoneNumbers: (value: string[] | null) => void;
  atcToken?: string | null;
  selectedPhoneNumbers: string[] | null;
  form: FormInstance;
  editableIntegration?: ModelSocialIntegration | undefined;
  integrationSocialType: string;
  disabled?: boolean;
  isOpen?: boolean;
};

export const AtcPhoneNumbersBlock: React.FC<AtcPhoneNumbersBlockProps> = ({
  setSelectedPhoneNumbers,
  atcToken,
  selectedPhoneNumbers,
  form,
  editableIntegration,
  integrationSocialType,
  disabled,
  isOpen,
}) => {
  const {
    params: { ActiveBranchId },
    activeBranchName,
  } = useUserPrint();
  const { showNotification } = useNotification();
  const [beelineAbonentNumbers, setBeelineAbonentNumbers] = useState<
    NSocialIntegrations.ModelAtcAbonent[]
  >([]);

  const [
    getAtcBeelineAbonents,
    {
      isLoading: isBeelineAbonentNumbersLoading,
      isFetching: isBeelineAbonentNumbersFetching,
      error: beelineAbonentNumbersError,
      isError: isBeelineAbonentNumbersError,
    },
  ] = useLazyGetAtcBeelineAbonentsQuery();

  const integrationId = useMemo(() => {
    return editableIntegration?.Id;
  }, [editableIntegration?.Id]);

  const getAtcBeelineAbonentsHandler = useCallback(async () => {
    if (!ActiveBranchId) return;
    if (!atcToken) return;
    if (!isOpen) return;
    try {
      const response = await getAtcBeelineAbonents({
        ActiveBranchId,
        integrationId,
        token: atcToken,
      }).unwrap();
      setBeelineAbonentNumbers(response);
    } catch (error) {
      showNotification(error);
    }
  }, [
    ActiveBranchId,
    atcToken,
    getAtcBeelineAbonents,
    integrationId,
    showNotification,
    isOpen,
  ]);

  useEffect(() => {
    getAtcBeelineAbonentsHandler();
  }, [getAtcBeelineAbonentsHandler]);

  useEffect(() => {
    if (!isOpen) setBeelineAbonentNumbers([]);
    return () => setBeelineAbonentNumbers([]);
  }, [isOpen]);

  const mangoResult = useCheckMangoTokenAndListAbonetsQuery(
    { ActiveBranchId, token: atcToken! },
    {
      skip:
        integrationSocialType !== ATC_MANGO ||
        !atcToken ||
        !ActiveBranchId ||
        !isOpen,
    }
  );

  const {
    data: abonentNumbers,
    isLoading,
    error,
    isError,
  } = integrationSocialType === ATC_MANGO
    ? mangoResult
    : { data: undefined, isLoading: false };

  const checkboxOptions = useMemo(() => {
    if (!abonentNumbers) return [];

    return Object.keys(abonentNumbers).map((phoneNumber) => ({
      label: phoneNumber,
      value: phoneNumber,
      checked: abonentNumbers[phoneNumber],
    }));
  }, [abonentNumbers]);

  const defaultCheckedList = useMemo(() => {
    if (!abonentNumbers) return [];

    return Object.keys(abonentNumbers).filter(
      (phoneNumber) => abonentNumbers[phoneNumber]
    );
  }, [abonentNumbers]);

  const [checkedList, setCheckedList] = useState<string[]>(
    () => defaultCheckedList
  );

  const isIndeterminateCheckboxState = useMemo(
    () => checkedList.length > 0 && checkedList.length < checkboxOptions.length,
    [checkboxOptions.length, checkedList.length]
  );

  const checkAll = useMemo(
    () => checkboxOptions.length === checkedList.length,
    [checkboxOptions.length, checkedList.length]
  );

  useEffect(() => {
    setCheckedList(defaultCheckedList);
  }, [defaultCheckedList]);

  useEffect(() => {
    if (checkAll && checkboxOptions.length) {
      form.setFieldValue('phoneNumbersCheckboxGroup', true);
    }
  }, [checkAll, checkboxOptions.length, form]);

  useEffect(() => {
    setSelectedPhoneNumbers(
      checkboxOptions.filter(({ checked }) => checked).map(({ value }) => value)
    );
  }, [checkboxOptions, setSelectedPhoneNumbers]);

  const selectedPhoneNumbersLength = useMemo(() => {
    if (selectedPhoneNumbers) {
      return selectedPhoneNumbers.length;
    }

    return checkboxOptions.filter(({ checked }) => checked).length;
  }, [checkboxOptions, selectedPhoneNumbers]);

  const onCheckAllChange: CheckboxProps['onChange'] = useCallback(
    (e: CheckboxChangeEvent) => {
      const data = e.target.checked
        ? checkboxOptions.map(({ value }) => value)
        : [];

      setCheckedList(data);
      setSelectedPhoneNumbers(data);
    },
    [checkboxOptions, setSelectedPhoneNumbers]
  );

  const errorMessage = useMemo(() => {
    if (isError && error) {
      return handleCatchError(error);
    }

    if (isBeelineAbonentNumbersError && beelineAbonentNumbersError) {
      return handleCatchError(beelineAbonentNumbersError);
    }

    return null;
  }, [
    beelineAbonentNumbersError,
    isBeelineAbonentNumbersError,
    error,
    isError,
  ]);

  return (
    <div className="atc-phone-numbers-block">
      <div className="item-container phone-numbers-selector">
        {!!errorMessage && <Text type="danger">{errorMessage}</Text>}
        <Spin
          spinning={
            isLoading ||
            isBeelineAbonentNumbersLoading ||
            isBeelineAbonentNumbersFetching
          }
        >
          {integrationSocialType === ATC_BEELINE && (
            <BeelineAbonentsSelector
              abonents={beelineAbonentNumbers ?? []}
              activeBranchName={activeBranchName}
              disabled={disabled || isBeelineAbonentNumbersLoading}
              onSelectedChange={setSelectedPhoneNumbers}
            />
          )}

          {abonentNumbers &&
            Object.keys(abonentNumbers).length > 0 &&
            integrationSocialType === ATC_MANGO && (
              <>
                <Text strong className="label label-require">
                  Выберите телефонные номера, которые будут интегрированы с
                  филиалом {activeBranchName}
                </Text>
                <Text type="secondary">
                  Всего номеров: {checkboxOptions.length}. Выбрано номеров:{' '}
                  {selectedPhoneNumbersLength}
                </Text>
                <Form.Item
                  name="phoneNumbersCheckboxGroup"
                  rules={[
                    {
                      validator: () => {
                        if (
                          !checkedList.length &&
                          !isIndeterminateCheckboxState
                        ) {
                          return Promise.reject(
                            new Error(ERROR_REQUIRED_FIELD)
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                  valuePropName="checked"
                >
                  <Checkbox
                    indeterminate={isIndeterminateCheckboxState}
                    onChange={onCheckAllChange}
                    checked={checkAll}
                  >
                    Выбрать все
                  </Checkbox>
                </Form.Item>
                <div className="phone-numbers-checkbox-group-wrapper">
                  <Checkbox.Group
                    className="phone-numbers-checkbox-group"
                    options={checkboxOptions}
                    value={checkedList}
                    onChange={(values) => {
                      setSelectedPhoneNumbers(values);
                      setCheckedList(values);
                    }}
                  />
                </div>
              </>
            )}
        </Spin>
      </div>
    </div>
  );
};
